import { useState, useEffect, FormEventHandler } from "react";
import { useNavigate } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Lock,
  User,
  Phone,
  ArrowLeft,
  Lightbulb,
  Gift,
} from "lucide-react";import toast from "react-hot-toast";
import { Link } from 'react-router-dom';
import { registerService, type RegisterRequest } from "@/services/auth";
import { z } from "zod";
import { Button, FormField } from "../ui";
import { fontSize, fontWeight, textColor, borderColor } from "@/theme";
import { useSearchParams } from "react-router-dom";


// ─── Schema ───────────────────────────────────────────────────────────────────
const registerSchema = z
  .object({
    username: z.string().min(1, "Username is required").max(50, "Username too long"),
    email: z.string().email("Enter valid email").max(100, "Email too long"),
    phone: z.string().min(10, "Phone must be 10 digits").max(10, "Phone must be 10 digits"),
    password: z.string().min(8, "Password must be at least 8 characters"),
    confirmPassword: z.string().min(2, "Confirm password is required"),
    referralCode: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function getErrorMessage(res: any): string {
  if (res?.errors) {
    const firstError = Object.values(res.errors)[0];
    if (Array.isArray(firstError)) return firstError[0];
    return String(firstError);
  }
  if (res?.message) return res.message;
  if (res?.error) return res.error;
  return "Registration failed";
}

/** Generate username suggestions from a base name */
function generateUsernameSuggestions(base: string): string[] {
  const clean = base.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase();
  const year = new Date().getFullYear();
  const rand = () => Math.floor(10 + Math.random() * 90); // 2-digit number

  const candidates = [
    `${clean}${rand()}`,
    `${clean}${rand()}`,
    `${clean}_${rand()}`,
    `${clean}${year}`,
    `${clean}.${rand()}`,
    `the_${clean}`,
    `${clean}_official`,
    `${clean}${year % 100}`,
  ];

  // Remove duplicates and return 5 suggestions
  return [...new Set(candidates)].slice(0, 5);
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function Register() {
  const [loading, setLoading] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const navigate = useNavigate();
const [searchParams] = useSearchParams();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: "onSubmit",
    defaultValues: {
      username: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
      referralCode: "",
    },
  });

  useEffect(() => {
  const referral = searchParams.get("ref");

  if (referral) {
    setValue("referralCode", referral);
  }
}, [searchParams, setValue]);

  // Clear suggestions when user manually changes the username field
  const watchedUsername = watch("username");
  useEffect(() => {
    setSuggestions([]);
    clearErrors("username");
  }, [watchedUsername, clearErrors]);

  // ── Submit handler ──────────────────────────────────────────────────────
  const onSubmit: FormEventHandler<HTMLFormElement> = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    await handleSubmit(async (formData: RegisterFormData) => {
      setLoading(true);

      try {
        const payload: RegisterRequest = {
          Username: formData.username,
          Email: formData.email,
          Phone: formData.phone,
          Password: formData.password,
          Conform_Password: formData.confirmPassword,
          Refer: formData.referralCode,
        };
        const response = await registerService(payload);

        // Success
        if (response && !response.errors) {
          toast.success("Registration successful! Redirecting to login...", { duration: 2000 });
          setTimeout(() => navigate("/login", { replace: true }), 1500);
          return;
        }

        // API validation errors
        if (response?.errors && typeof response.errors === "object") {
          const apiErrors: Record<string, string> = {};

          Object.entries(response.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
              apiErrors[field] = messages[0] as string;
            }
          });

          const formFieldMap: Record<string, keyof RegisterFormData> = {
            Username: "username",
            Email: "email",
            Phone: "phone",
            Password: "password",
            Conform_Password: "confirmPassword",
            non_field_errors: "confirmPassword",
            Refer: "referralCode", 
          };

          Object.entries(apiErrors).forEach(([field, message]) => {
            const formField = formFieldMap[field as keyof typeof formFieldMap];
            if (formField) {
              setError(formField, { type: "server", message });
            }

            // ── Username conflict → generate suggestions ──────────────────
            if (
              field === "Username" &&
              message.toLowerCase().includes("already exists")
            ) {
              setSuggestions(generateUsernameSuggestions(formData.username));
            }
          });

          if (Object.keys(apiErrors).length === 0) {
            toast.error(getErrorMessage(response));
          }
        } else {
          toast.error(getErrorMessage(response));
        }
      } catch (err: unknown) {
        const error = err as Error;
        console.error("Registration error:", error);
        toast.error(error.message || "Registration failed. Please try again.");
      } finally {
        setLoading(false);
      }
    })(event);
  };

  // ── Apply suggestion ────────────────────────────────────────────────────
  const applySuggestion = (name: string) => {
    setValue("username", name, { shouldValidate: false });
    clearErrors("username");
    setSuggestions([]);
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-brand-100 py-12 px-4 sm:px-6 lg:px-8">
      {/* Top Navigation */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 relative rounded-full overflow-hidden border-4 border-orange-200 shadow-2xl">
                <img src="/mlblogo.jpeg" alt="Mera Lunch Box" className="w-full h-full object-cover" />
              </div>
              <h1 className={`${fontSize['2xl']} ${fontWeight.extrabold} ${textColor.heading} tracking-tight`}>
                Mera Lunch Box
              </h1>
            </div>
          </div>
          <Link
            to="/login"
            className={`flex items-center ${textColor.primary} hover:text-brand-700 ${fontWeight.bold} transition-all duration-200 hover:scale-105 shadow-md px-4 py-2 rounded-xl bg-white/50 backdrop-blur-sm border border-brand-100`}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            LOGIN
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.h1
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-4xl md:text-5xl font-black text-gray-900 pb-6 bg-gradient-to-r from-gray-900 via-brand-900 to-brand-900 bg-clip-text text-transparent drop-shadow-2xl"
          >
            Register
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`${fontSize.xl} ${textColor.muted} max-w-sm mx-auto leading-relaxed`}
          >
            Create your account to get started with our platform
          </motion.p>
        </div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="bg-white/90 backdrop-blur-2xl rounded-3xl shadow-2xl border border-white/30 p-8 relative"
        >
          {/* Decorative element — clipped to its own box so it never affects layout */}
          <div className="absolute top-4 right-4 w-24 h-24 overflow-hidden rounded-3xl -z-10 pointer-events-none">
            <div className="w-full h-full bg-gradient-to-br from-brand-400/20 to-brand-400/20 blur-xl" />
          </div>

          <form onSubmit={onSubmit} className="space-y-8" noValidate>
            {/* Row 1: Username & Email */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Username with floating suggestion dropdown */}
              <div className="relative">
                <FormField
                  control={control}
                  name="username"
                  label="Username"
                  placeholder="Enter your username"
                  icon={<User className={`w-5 h-5 ${textColor.icon}`} />}
                  error={errors.username?.message}
                  onClear={() => { clearErrors("username"); setSuggestions([]); }}
                />

                {/* Absolute dropdown — floats over UI, does not affect layout */}
                <AnimatePresence>
                  {suggestions.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute left-0 right-0 top-full mt-1 z-50 bg-white border border-brand-200 rounded-xl shadow-lg p-3"
                    >
                      <div className="flex items-center gap-1.5 mb-2.5">
                        <Lightbulb className={`w-3.5 h-3.5 ${textColor.primary}`} />
                        <span className={`${fontSize.xs} ${fontWeight.semibold} ${textColor.primary}`}>
                          Suggested usernames
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.map((s) => (
                          <button
                            key={s}
                            type="button"
                            onClick={() => applySuggestion(s)}
                            className={`px-3 py-1 ${fontSize.xs} ${fontWeight.medium} bg-white border ${borderColor.primary} ${textColor.primary} rounded-full hover:bg-brand-500 hover:text-white hover:border-brand-500 transition-colors`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <FormField
                  control={control}
                  name="email"
                  label="Email"
                  placeholder="your@email.com"
                  type="email"
                  icon={<Mail className={`w-5 h-5 ${textColor.icon}`} />}
                  error={errors.email?.message}
                  onClear={() => clearErrors("email")}
                />
              </div>
            </div>

            {/* Row 2: Phone & Referral Code */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <FormField
                  control={control}
                  name="phone"
                  label="Phone"
                  placeholder="10-digit phone number"
                  type="tel"
                  maxLength={10}
                  icon={<Phone className={`w-5 h-5 ${textColor.icon}`} />}
                  error={errors.phone?.message}
                  onClear={() => clearErrors("phone")}
                />
              </div>
              <div>
                <FormField
                  control={control}
                  name="referralCode"
                  label="Referral Code"
                  placeholder="Enter referral code"
                  icon={<Gift className={`w-5 h-5 ${textColor.icon}`} />}
                  error={errors.referralCode?.message}
                  onClear={() => clearErrors("referralCode")}
                />

                <p className="text-xs text-brand-500 mt-2 font-medium">
                  Have a referral code? Apply it and earn rewards 🎉
                </p>
              </div>
            </div>

            {/* Row 3: Password & Confirm Password */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <FormField
                  control={control}
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  type="password"
                  icon={<Lock className={`w-5 h-5 ${textColor.icon}`} />}
                  error={errors.password?.message}
                  onClear={() => clearErrors("password")}
                />
              </div>
              <div>
                <FormField
                  control={control}
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="Confirm your password"
                  type="password"
                  icon={<Lock className={`w-5 h-5 ${textColor.icon}`} />}
                  error={errors.confirmPassword?.message}
                  onClear={() => clearErrors("confirmPassword")}
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-2 flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                type="submit"
                disabled={loading || isSubmitting}
                size="lg"
                className="w-full sm:w-auto px-12 h-14 text-lg font-bold bg-gradient-to-r from-blue-600 via-brand-700 to-brand-700 hover:from-blue-700 hover:via-brand-800 hover:to-brand-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl transition-all duration-300 transform hover:-translate-y-0.5"
              >
                {loading || isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-4 h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Account...
                  </>
                ) : (
                  "Create Account"
                )}
              </Button>
            </div>
          </form>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className={`${textColor.muted} ${fontSize.lg}`}>
            Already have an account?{" "}
            <Link
              to="/login"
              className={`${fontWeight.bold} ${textColor.primary} hover:text-brand-700 transition-all duration-200 underline decoration-2 underline-offset-4 hover:decoration-brand-500`}
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}